import { z } from 'zod';

/**
 * Source of a version snapshot — who/what created it.
 * - `user`: manual save by a human
 * - `chat`: saved by an AI chat tool call
 * - `system`: auto-saved (e.g. before a restore operation)
 */
export const VersionSourceSchema = z.enum(['user', 'chat', 'system']);
export type VersionSource = z.infer<typeof VersionSourceSchema>;

/**
 * Publish state derived from an entity's `publishedVersionId` and
 * whether its draft data matches the published snapshot.
 */
export const PublishStateSchema = z.enum(['unpublished', 'published_clean', 'published_dirty']);
export type PublishState = z.infer<typeof PublishStateSchema>;

/**
 * Summary of a single version — returned by list endpoints.
 * Does not include the full data blob (use `VersionDetail` for that).
 */
export const VersionSummarySchema = z.object({
  id: z.string(),
  entityId: z.string(),
  versionNumber: z.number().int().positive(),
  note: z.string().max(500).nullable(),
  createdByUserId: z.string().nullable(),
  createdBySource: VersionSourceSchema,
  createdAt: z.number(),
});
export type VersionSummary = z.infer<typeof VersionSummarySchema>;

/**
 * Full version detail — includes the opaque data snapshot.
 */
export const VersionDetailSchema = VersionSummarySchema.extend({
  data: z.unknown(),
});
export type VersionDetail = z.infer<typeof VersionDetailSchema>;

/**
 * Longest before/after value a diff carries per field. Snapshots hold whole
 * prompt templates and JSON schemas; sending them verbatim would make the
 * diff response (and the publish dialog rendering it) unbounded, so values
 * are truncated at the producer and the UI shows the head of each side.
 */
export const MAX_DIFF_VALUE_LENGTH = 400;

/** How one thing changed between the published snapshot and the draft. */
export const VersionChangeKindSchema = z.enum(['added', 'removed', 'modified']);
export type VersionChangeKind = z.infer<typeof VersionChangeKindSchema>;

/**
 * One field's before/after inside a `modified` change. Values are already
 * stringified and truncated by the producer (see {@link MAX_DIFF_VALUE_LENGTH});
 * `null` means the field was absent on that side.
 */
export const VersionFieldChangeSchema = z.object({
  /** Machine path within the changed thing, e.g. `config.promptTemplate`. */
  field: z.string(),
  /** Human label for the field, e.g. `Prompt`. */
  label: z.string(),
  before: z.string().nullable(),
  after: z.string().nullable(),
});
export type VersionFieldChange = z.infer<typeof VersionFieldChangeSchema>;

/**
 * One entry in the "what changed" list a publish surface renders. Entity
 * packages decide what a change is about (`target`) — flows emit `node`,
 * `edge`, and `settings` — so the shape stays generic across versioned
 * entities while the UI can still group and label meaningfully.
 */
export const VersionChangeSchema = z.object({
  /** Stable key within one diff, e.g. `node:agent-1`. */
  id: z.string(),
  kind: VersionChangeKindSchema,
  /** Coarse category of the changed thing (`node` / `edge` / `settings`). */
  target: z.string(),
  /** Human label for the changed thing, e.g. the node's name. */
  label: z.string(),
  /** Short qualifier rendered beside the label, e.g. the node type. */
  detail: z.string().nullable(),
  /** Field-level before/after. Populated for `modified`, empty otherwise. */
  fields: z.array(VersionFieldChangeSchema),
});
export type VersionChange = z.infer<typeof VersionChangeSchema>;

/**
 * Summary of differences between a draft and its published version.
 *
 * The counts are the headline; `changes` is the itemized list behind it.
 * It may be shorter than `added + removed + modified` — producers cap it so
 * a wholesale graph rewrite can't return an unbounded payload — so treat the
 * counts, not `changes.length`, as the totals.
 */
export const DiffSummarySchema = z.object({
  isUnpublished: z.boolean(),
  isDirty: z.boolean(),
  added: z.number().int().nonnegative(),
  removed: z.number().int().nonnegative(),
  modified: z.number().int().nonnegative(),
  changes: z.array(VersionChangeSchema),
});
export type DiffSummary = z.infer<typeof DiffSummarySchema>;

/**
 * Suggested title for a pending change, offered when publishing a new
 * version. `generated: false` means the LLM task was unavailable (no key,
 * disabled, or errored) and `title` is the deterministic fallback built from
 * the diff counts — the caller can still publish with it.
 */
export const ChangeTitleSchema = z.object({
  title: z.string(),
  generated: z.boolean(),
});
export type ChangeTitle = z.infer<typeof ChangeTitleSchema>;

/**
 * Optional body for `POST /flows/:id/change-title`. `previousTitle` is the
 * suggestion already on screen when the caller asks for another one, so the
 * model can avoid repeating it. Only that one title is sent; a later click
 * can therefore return an earlier suggestion the model was not told to avoid.
 */
export const ChangeTitleRequestSchema = z.object({
  previousTitle: z.string().max(500).optional(),
});
export type ChangeTitleRequest = z.infer<typeof ChangeTitleRequestSchema>;
